/**
 * 认证徽标判定纯函数（移植自小程序 components/my/merchantBadge.js）
 *
 * 数据来源：GET /login/getAuthInformation 的 data：
 *   authStatus     平台认证状态：0 未认证 / 1 审核中 / 2 认证失败 / 3 已认证
 *   authType       平台认证类型（已认证时有值）：1 个人 / 2 企业 / 3 机构
 *   merchantStatus 二级商户状态：1 审核中 / 2 待签约 / 3 已认证 / 4 已驳回；0/5/6/缺失为非商户流程态
 *   merchantType   商户类型：1 小微 / 2 个体工商户 / 3 企业 / 4 个人卖家 / 5 事业单位 / 6 政府机关 / 7 社会组织
 *
 * 后端可能以字符串形式返回状态值，统一 Number() 后再比较，不复刻小程序侧严格 === 的行为。
 */

// 徽标图标（OSS 直链，与小程序共用）
export const AUTH_BADGE_ICON = {
  noAuth: "https://ossfastmake.xlkapp.com/12jielong/bdJL/no-auth.png",
  authing: "https://ossfastmake.xlkapp.com/12jielong/bdJL/authing.png",
  authQy: "https://ossfastmake.xlkapp.com/12jielong/bdJL/auth-qy.png",
};

// 商户认证成功文案（merchantType → 文案；缺失/未知兜底「商户认证」）
const MERCHANT_TYPE_TEXT = {
  1: "小微商户",
  2: "个体工商户",
  3: "企业",
  4: "个人卖家",
  5: "事业单位",
  6: "政府机关",
  7: "社会组织",
};

// 商户认证成功底色（仅 1/2/3/5 内联覆盖，其余用默认紫渐变；与文案是两张独立映射表）
const MERCHANT_TYPE_BACKGROUND = {
  1: "#9885FA",
  2: "#E25C9E",
  3: "#1EB86F",
  5: "#7FBEF8",
};

// 二级商户流程状态（merchantStatus → 徽章状态；0/5/6/缺失/非数字 → null）
export function merchantBadgeState(merchantStatus) {
  const value = Number(merchantStatus);
  if (Number.isNaN(value)) return null;
  const stateMap = { 1: "reviewing", 2: "signing", 3: "certified", 4: "rejected" };
  return stateMap[value] || null;
}

// 商户流程是否进行中（进行中时平台认证徽标全部隐藏）
export function isMerchantFlowActive(merchantStatus) {
  return merchantBadgeState(merchantStatus) !== null;
}

function badge(variant, text, icon, background) {
  return { variant, text, icon, background: background || null };
}

/**
 * 徽章判定（决策表见 doc/feature/auth-badge-pc-handoff.md §3.1，按优先级命中即停止）：
 * 1. 商户流程（merchantStatus 1-4）优先，平台认证徽标全部压制；
 * 2. 平台：authStatus=1 → 审核中；authStatus=3 + authType 1/2/3 → 个人/企业/机构认证
 *    （authType 缺失或未知 → null 不显示徽章）；authStatus=0/2/其他 → 未认证。
 * 返回 { variant, text, icon, background } 或 null（不显示徽章）。
 */
export function resolveAuthBadge({ authStatus, authType, merchantStatus, merchantType } = {}) {
  const merchantState = merchantBadgeState(merchantStatus);
  if (merchantState === "reviewing") {
    return badge("authing", "审核中", AUTH_BADGE_ICON.authing);
  }
  if (merchantState === "signing") {
    return badge("dq", "待签约", "");
  }
  if (merchantState === "rejected") {
    return badge("bh", "已驳回", "");
  }
  if (merchantState === "certified") {
    const type = Number(merchantType);
    return badge("sh", MERCHANT_TYPE_TEXT[type] || "商户认证", AUTH_BADGE_ICON.authQy, MERCHANT_TYPE_BACKGROUND[type]);
  }

  const status = Number(authStatus);
  if (status === 1) {
    return badge("authing", "审核中", AUTH_BADGE_ICON.authing);
  }
  if (status === 3) {
    const type = Number(authType);
    if (type === 1) return badge("gr", "个人认证", AUTH_BADGE_ICON.authQy);
    if (type === 2) return badge("qy", "企业认证", AUTH_BADGE_ICON.authQy);
    if (type === 3) return badge("jg", "机构认证", AUTH_BADGE_ICON.authQy);
    return null;
  }
  return badge("no-auth", "未认证", AUTH_BADGE_ICON.noAuth);
}

/**
 * 徽标点击行为（§5，按顺序命中即返回）：
 * - 'merchant'：merchantStatus ∈ [1..6] → 商户申请状态页（5/6 为后端扩展态，统一归商户流程）
 * - null：authStatus=1（平台审核中）且非商户流程 → 无动作（与小程序现状一致）
 * - 'success'：authStatus=3 → 认证成功页
 * - 'choose'：未认证/认证失败（authStatus 0/2/缺失，含 merchantStatus=0）→ 认证类型选择
 * PC 端暂无认证流程页面，三种目标均映射为「前往小程序」引导。
 */
export function resolveAuthClickAction({ authStatus, merchantStatus } = {}) {
  const merchant = Number(merchantStatus);
  if (merchant >= 1 && merchant <= 6) return "merchant";
  if (Number(authStatus) === 1) return null;
  if (Number(authStatus) === 3) return "success";
  return "choose";
}
